import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatNavList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { MbscDatepickerModule } from '@mobiscroll/angular';
import { BannerModule } from '@sb/library/banner';
import { BaseModule } from '@sb/library/base';
import { ButtonModule } from '@sb/library/button';
import { CardModule } from '@sb/library/card';
import { IconModule } from '@sb/library/icon';
import { LayoutModule } from '@sb/library/layout';
import { LocalizationModule } from '@sb/library/localization';
import { PopoverModule } from '@sb/library/popover';
import { SubmissionBarModule } from '@sb/library/submission-bar';
import { UtilityModule } from '@sb/library/utility';
import { FormFieldCheckboxGroupItemComponent } from './checkbox-group/form-field-checkbox-group-item.component';
import { FormFieldCheckboxGroupComponent } from './checkbox-group/form-field-checkbox-group.component';
import { FormFieldCheckboxMultiSelectComponent } from './checkbox-multi-select/form-field-checkbox-multi-select.component';
import { FormFieldCheckboxComponent } from './checkbox/form-field-checkbox.component';
import { SplitCheckboxComponent } from './checkbox/split-checkbox/split-checkbox.component';
import { FormFieldInputContainerComponent } from './container/form-field-container.component';
import { FormFieldPaypalContainerComponent } from './container/paypal-container/form-field-paypal-container.component';
import { ErrorComponent } from './error/error.component';
import { AutocompletePositionDirective } from './input/autocomplete-position.directive';
import { FormFieldBillingProfileComponent } from './input/billing-profile/form-field-billing-profile.component';
import { ColorPickerComponent } from './input/color-picker/color-picker.component';
import { FormFieldCounterComponent } from './input/counter/form-field-counter.component';
import { FormFieldCurrencyComponent } from './input/currency/form-field-currency.component';
import { FormFieldMonthYearComponent } from './input/date/month-year/form-field-month-year.component';
import { DateRangeSelectorComponent } from './input/date/range-selector/date-range-selector.component';
import { FormFieldDateRangeComponent } from './input/date/range/form-field-date-range.component';
import { DateSelectorComponent } from './input/date/selector/date-selector.component';
import { FormFieldDateComponent } from './input/date/single/form-field-date.component';
import { FormFieldDecimalComponent } from './input/decimal/form-field-decimal.component';
import { FormFieldInvoiceNumberFormatComponent } from './input/invoice-number-format/form-field-invoice-number-format.component';
import { FormFieldNumberComponent } from './input/number/form-field-number.component';
import { FormFieldSecretComponent } from './input/password/form-field-password.component';
import { FormFieldPercentComponent } from './input/percent/form-field-percent.component';
import { EditRecurrenceDialogComponent } from './input/recurrence-picker/edit-recurrence-dialog/edit-recurrence-dialog.component';
import { FormFieldRecurrencePickerComponent } from './input/recurrence-picker/form-field-recurrence-picker.component';
import { SelectPositionDirective } from './input/select-position.directive';
import { FormFieldSelectAutoCompleteComponent } from './input/select/autocomplete/form-field-select-autocomplete.component';
import { ChipPreviewComponent } from './input/select/chips/chip-preview/form-field-chip-preview.component';
import { FormFieldSelectInputChipsComponent } from './input/select/chips/chip-select-input/form-field-select-input-chips.component';
import { FormFieldSelectChipsComponent } from './input/select/chips/chip-select/form-field-select-chips.component';
import { FormFieldSelectItemGroupComponent } from './input/select/form-field-select-item-group.component';
import { FormFieldSelectItemComponent } from './input/select/form-field-select-item.component';
import { FormFieldSelectComponent } from './input/select/single/form-field-select.component';
import { MatOptionHeightDirective } from './input/select/single/mat-option-height.directive';
import { FormFieldTaxComponent } from './input/tax/tax.component';
import { FormFieldTextAreaComponent } from './input/text-area/form-field-text-area.component';
import { FormFieldTextComponent } from './input/text/form-field-text.component';
import { FormFieldTextSearchComponent } from './input/text/search/form-field-text-search.component';
import { FormFieldDurationComponent } from './input/time/duration/form-field-duration.component';
import { FormFieldTimeComponent } from './input/time/form-field-time.component';
import { LabelComponent } from './label/label.component';
import { FormFieldPillSelectGroupItemComponent } from './pill-select-group/form-field-pill-select-group-item.component';
import { FormFieldPillSelectGroupComponent } from './pill-select-group/form-field-pill-select-group.component';
import { FormFieldPillSelectComponent } from './pill-select/form-field-pill-select.component';
import { FormFieldRadioCardComponent } from './radio-card/form-field-radio-card.component';
import { FormFieldRadioCardGroupComponent } from './radio-card/group/form-field-radio-card-group.component';
import { FormFieldRadioGroupItemComponent } from './radio-group/form-field-radio-group-item.component';
import { FormFieldRadioGroupComponent } from './radio-group/form-field-radio-group.component';
import { ReadOnlyComponent } from './read-only/read-only.component';
import { SegmentedButtonItemComponent } from './segmented-button/item/segmented-button-item.component';
import { FormFieldSegmentedButtonComponent } from './segmented-button/segmented-button.component';
import { FormFieldStarRatingComponent } from './star-rating/form-field-star-rating.component';
import { SupportingTextComponent } from './supporting-text/supporting-text.component';
import { FormFieldSwitchComponent } from './switch/form-field-switch.component';
import { A11yModule } from "@angular/cdk/a11y";



@NgModule({
    declarations: [
        AutocompletePositionDirective,
        SelectPositionDirective,
        FormFieldInputContainerComponent,
        FormFieldCheckboxComponent,
        FormFieldCheckboxGroupComponent,
        FormFieldCheckboxGroupItemComponent,
        FormFieldCounterComponent,
        FormFieldNumberComponent,
        FormFieldDecimalComponent,
        FormFieldCurrencyComponent,
        FormFieldTextComponent,
        FormFieldTextAreaComponent,
        FormFieldDateComponent,
        FormFieldDateRangeComponent,
        FormFieldMonthYearComponent,
        FormFieldRadioGroupComponent,
        FormFieldRadioGroupItemComponent,
        FormFieldSelectItemComponent,
        FormFieldSelectItemGroupComponent,
        FormFieldSelectComponent,
        FormFieldSelectChipsComponent,
        FormFieldSelectInputChipsComponent,
        FormFieldSelectAutoCompleteComponent,
        FormFieldSecretComponent,
        FormFieldTaxComponent,
        ColorPickerComponent,
        DateSelectorComponent,
        ErrorComponent,
        SupportingTextComponent,
        LabelComponent,
        ReadOnlyComponent,
        FormFieldPercentComponent,
        MatOptionHeightDirective,
        FormFieldDurationComponent,
        FormFieldTimeComponent,
        ChipPreviewComponent,
        FormFieldRecurrencePickerComponent,
        FormFieldBillingProfileComponent,
        FormFieldInvoiceNumberFormatComponent,
        DateRangeSelectorComponent,
        SplitCheckboxComponent,
        FormFieldSegmentedButtonComponent,
        SegmentedButtonItemComponent,
        FormFieldSwitchComponent,
        FormFieldTextSearchComponent,
        FormFieldPillSelectComponent,
        FormFieldPillSelectGroupItemComponent,
        FormFieldPillSelectGroupComponent,
        FormFieldRadioCardGroupComponent,
        FormFieldRadioCardComponent,
        FormFieldCheckboxMultiSelectComponent,
        FormFieldPaypalContainerComponent,
        EditRecurrenceDialogComponent,
        FormFieldStarRatingComponent
    ],
    imports: [
    CommonModule,
    IconModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatSelectModule,
    MatChipsModule,
    MatMenuModule,
    MatAutocompleteModule,
    MatDividerModule,
    LocalizationModule,
    MbscDatepickerModule,
    ButtonModule,
    ColorPickerModule,
    BaseModule,
    UtilityModule,
    OverlayModule,
    BannerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    CardModule,
    MatNavList,
    PopoverModule,
    SubmissionBarModule,
    LayoutModule,
    A11yModule
],
    exports: [
        AutocompletePositionDirective,
        SelectPositionDirective,
        FormFieldInputContainerComponent,
        FormFieldCheckboxComponent,
        FormFieldCounterComponent,
        FormFieldCheckboxGroupComponent,
        FormFieldCheckboxGroupItemComponent,
        FormFieldNumberComponent,
        FormFieldDecimalComponent,
        FormFieldCurrencyComponent,
        FormFieldTextComponent,
        FormFieldTextAreaComponent,
        FormFieldDateComponent,
        FormFieldDateRangeComponent,
        FormFieldDurationComponent,
        FormFieldMonthYearComponent,
        FormFieldRadioGroupComponent,
        FormFieldRadioGroupItemComponent,
        FormFieldSelectItemComponent,
        FormFieldSelectItemGroupComponent,
        FormFieldSelectComponent,
        FormFieldSelectChipsComponent,
        FormFieldSelectInputChipsComponent,
        FormFieldSelectAutoCompleteComponent,
        FormFieldSecretComponent,
        FormFieldTaxComponent,
        ColorPickerComponent,
        DateSelectorComponent,
        ErrorComponent,
        SupportingTextComponent,
        LabelComponent,
        FormFieldPercentComponent,
        MatOptionHeightDirective,
        FormFieldRecurrencePickerComponent,
        FormFieldInvoiceNumberFormatComponent,
        FormFieldTimeComponent,
        FormFieldRecurrencePickerComponent,
        DateRangeSelectorComponent,
        FormFieldRecurrencePickerComponent,
        FormFieldBillingProfileComponent,
        SplitCheckboxComponent,
        FormFieldSegmentedButtonComponent,
        SegmentedButtonItemComponent,
        FormFieldSwitchComponent,
        FormFieldTextSearchComponent,
        FormFieldRadioCardGroupComponent,
        FormFieldRadioCardComponent,
        FormFieldCheckboxMultiSelectComponent,
        FormFieldPaypalContainerComponent,
        FormFieldPillSelectComponent,
        FormFieldPillSelectGroupItemComponent,
        FormFieldPillSelectGroupComponent,
        FormFieldStarRatingComponent
    ]
})
export class FormFieldModule { }
